import React, { useState, useEffect } from 'react';
import './BulkEmailPage.css';
import { useNavigate } from 'react-router-dom';
const BulkEmailPage = ({ user, setUser, handleLogout }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [targetGroup, setTargetGroup] = useState('all_users');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    paidUsers: 0,
    questionnaireCompleted: 0,
    noQuestionnaire: 0
  });
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

   // Admin check
   useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
  }, [user, navigate]);
  // Fetch user group statistics
  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        const response = await fetch(`${apiUrl}/users/user-groups-stats`);
        if (response.ok) {
          const stats = await response.json();
          setUserStats(stats);
        }
      } catch (error) {
        console.error('Error fetching user stats:', error);
      }
    };

    fetchUserStats();
  }, [apiUrl]);

  const handleSendEmail = async () => {
    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!message.trim()) {
      setError('Message is required');
      return;
    }

    const groupNames = {
      'all_users': 'all users',
      'paid_users': 'paid users',
      'questionnaire_completed': 'users who completed questionnaire but haven\'t paid',
      'no_questionnaire': 'users who haven\'t submitted questionnaire'
    };

    if (!window.confirm(`Are you sure you want to send this email to ${groupNames[targetGroup]}?`)) {
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${apiUrl}/users/send-bulk-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: subject.trim(),
          message: message.trim(),
          targetGroup: targetGroup
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Email sent successfully to ${data.sentCount} users in ${groupNames[targetGroup]} group!`);
        setSubject('');
        setMessage('');
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.error || 'Failed to send emails');
      }
    } catch (error) {
      console.error('Error sending bulk email:', error);
      setError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const getTemplatesByGroup = () => {
    switch (targetGroup) {
      case 'paid_users':
        return [
          {
            icon: '🎉',
            title: 'Thank You',
            subject: 'Thank You for Your Payment - Premium Access Activated',
            message: 'Hello!\n\nThank you for your payment! Your premium access has been activated and you now have full access to all university recommendations and detailed information.\n\nExplore your personalized recommendations and start your application process.\n\nBest regards,\nAdmissionsExpress Team'
          },
          {
            icon: '📚',
            title: 'New Features',
            subject: 'New Premium Features Available - Check Them Out!',
            message: 'Hi there!\n\nWe\'ve added exciting new premium features just for you! As a premium member, you now have access to:\n\n• Advanced filtering options\n• Detailed university comparisons\n• Application deadline reminders\n\nLog in to explore these new features.\n\nBest regards,\nAdmissionsExpress Team'
          }
        ];
      
      case 'questionnaire_completed':
        return [
          {
            icon: '🔓',
            title: 'Unlock Premium',
            subject: 'Unlock Full University Details - Upgrade to Premium',
            message: 'Hello!\n\nYou\'ve completed your questionnaire and we have great university recommendations for you! However, detailed information is currently hidden.\n\nUpgrade to premium to unlock:\n• Full university details\n• Contact information\n• Application requirements\n• Scholarship opportunities\n\nUpgrade now to access everything!\n\nBest regards,\nAdmissionsExpress Team'
          },
          {
            icon: '⏰',
            title: 'Limited Offer',
            subject: 'Limited Time Offer - 20% Off Premium Access',
            message: 'Hi there!\n\nSpecial offer just for you! Get 20% off premium access and unlock all university details.\n\nThis limited-time offer expires soon. Don\'t miss out on accessing complete information about your recommended universities.\n\nUse code: SAVE20\n\nBest regards,\nAdmissionsExpress Team'
          }
        ];
      
      case 'no_questionnaire':
        return [
          {
            icon: '📝',
            title: 'Complete Profile',
            subject: 'Complete Your Questionnaire - Get Personalized Recommendations',
            message: 'Hello!\n\nWelcome to AdmissionsExpress! To get started with personalized university recommendations, please complete your academic questionnaire.\n\nIt only takes 5 minutes and you\'ll receive tailored suggestions based on your preferences and qualifications.\n\nStart your questionnaire now!\n\nBest regards,\nAdmissionsExpress Team'
          },
          {
            icon: '🚀',
            title: 'Get Started',
            subject: 'Your Study Abroad Journey Starts Here',
            message: 'Hi there!\n\nReady to find your perfect university? Complete your profile questionnaire to unlock personalized recommendations from thousands of universities worldwide.\n\nOur smart matching system will find universities that match your:\n• Academic background\n• Budget preferences\n• Location preferences\n• Career goals\n\nGet started today!\n\nBest regards,\nAdmissionsExpress Team'
          }
        ];
      
      default:
        return [
          {
            icon: '👋',
            title: 'Welcome Email',
            subject: 'Welcome to AdmissionsExpress!',
            message: 'Hello!\n\nWelcome to AdmissionsExpress! We are excited to help you with your study abroad journey.\n\nGet started by completing your profile and exploring university recommendations.\n\nBest regards,\nAdmissionsExpress Team'
          },
          {
            icon: '🎓',
            title: 'New Updates',
            subject: 'New Universities Added - Explore Now!',
            message: 'Great news!\n\nWe\'ve added new universities and programs to our platform. Explore new opportunities that match your interests and career goals.\n\nLog in to your dashboard to discover these new options.\n\nBest regards,\nAdmissionsExpress Team'
          }
        ];
    }
  };

  return (
    <div className="bulk-email-page">
      {/* Header */}
      <header className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <span className="title-icon">📧</span>
            Bulk Email System
          </h1>
          <p className="page-subtitle">Send targeted emails to specific user groups</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="page-main">
        <div className="email-container">
          {/* User Statistics */}
          <div className="stats-section">
            <h3 className="stats-title">User Group Statistics</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-icon">👥</span>
                <span className="stat-number">{userStats.totalUsers}</span>
                <span className="stat-label">Total Users</span>
              </div>
              <div className="stat-card">
                <span className="stat-icon">💳</span>
                <span className="stat-number">{userStats.paidUsers}</span>
                <span className="stat-label">Paid Users</span>
              </div>
              <div className="stat-card">
                <span className="stat-icon">📝</span>
                <span className="stat-number">{userStats.questionnaireCompleted}</span>
                <span className="stat-label">Questionnaire Done</span>
              </div>
              <div className="stat-card">
                <span className="stat-icon">❌</span>
                <span className="stat-number">{userStats.noQuestionnaire}</span>
                <span className="stat-label">No Questionnaire</span>
              </div>
            </div>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="message error-message">
              <span className="message-icon">❌</span>
              <span className="message-text">{error}</span>
              <button className="message-close" onClick={clearMessages}>×</button>
            </div>
          )}
          
          {success && (
            <div className="message success-message">
              <span className="message-icon">✅</span>
              <span className="message-text">{success}</span>
              <button className="message-close" onClick={clearMessages}>×</button>
            </div>
          )}

          {/* Email Form */}
          <div className="email-form-card">
            <div className="card-header">
              <h2 className="card-title">Compose Email</h2>
              <p className="card-subtitle">Select target group and compose your message</p>
            </div>

            <form className="email-form" onSubmit={(e) => e.preventDefault()}>
              {/* Target Group Selection */}
              <div className="form-group">
                <label className="form-label" htmlFor="targetGroup">
                  <span className="label-text">Target Group</span>
                  <span className="label-required">*</span>
                </label>
                <div className="input-wrapper">
                  <select
                    id="targetGroup"
                    className="form-select"
                    value={targetGroup}
                    onChange={(e) => setTargetGroup(e.target.value)}
                    disabled={sending}
                  >
                    <option value="all_users">All Users ({userStats.totalUsers})</option>
                    <option value="paid_users">Paid Users ({userStats.paidUsers})</option>
                    <option value="questionnaire_completed">Questionnaire Completed - No Payment ({userStats.questionnaireCompleted})</option>
                    <option value="no_questionnaire">No Questionnaire Submitted ({userStats.noQuestionnaire})</option>
                  </select>
                </div>
                <div className="group-description">
                  {targetGroup === 'all_users' && (
                    <p className="description-text">📢 Send to all registered users on the platform</p>
                  )}
                  {targetGroup === 'paid_users' && (
                    <p className="description-text">💳 Send to users who have completed payment and have premium access</p>
                  )}
                  {targetGroup === 'questionnaire_completed' && (
                    <p className="description-text">📝 Send to users who completed questionnaire but haven't made payment yet</p>
                  )}
                  {targetGroup === 'no_questionnaire' && (
                    <p className="description-text">❌ Send to users who haven't submitted their questionnaire yet</p>
                  )}
                </div>
              </div>

              {/* Subject Field */}
              <div className="form-group">
                <label className="form-label" htmlFor="subject">
                  <span className="label-text">Subject</span>
                  <span className="label-required">*</span>
                </label>
                <div className="input-wrapper">
                  <input
                    id="subject"
                    type="text"
                    className="form-input"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Enter email subject..."
                    disabled={sending}
                    maxLength="200"
                  />
                  <div className="input-counter">
                    {subject.length}/200
                  </div>
                </div>
              </div>

              {/* Message Field */}
              <div className="form-group">
                <label className="form-label" htmlFor="message">
                  <span className="label-text">Message</span>
                  <span className="label-required">*</span>
                </label>
                <div className="input-wrapper">
                  <textarea
                    id="message"
                    className="form-textarea"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your message here...&#10;&#10;You can write multiple lines.&#10;The message will be sent to the selected user group."
                    disabled={sending}
                    rows="8"
                    maxLength="2000"
                  />
                  <div className="input-counter">
                    {message.length}/2000
                  </div>
                </div>
              </div>

              {/* Quick Templates */}
              <div className="templates-section">
                <h3 className="templates-title">Quick Templates for {targetGroup.replace('_', ' ').toUpperCase()}</h3>
                <div className="templates-grid">
                  {getTemplatesByGroup().map((template, index) => (
                    <button
                      key={index}
                      type="button"
                      className="template-btn"
                      onClick={() => {
                        setSubject(template.subject);
                        setMessage(template.message);
                      }}
                      disabled={sending}
                    >
                      <span className="template-icon">{template.icon}</span>
                      {template.title}
                    </button>
                  ))}
                </div>
              </div>

              {/* Send Button */}
              <div className="form-actions">
                <button
                  type="button"
                  className="send-button"
                  onClick={handleSendEmail}
                  disabled={sending || !subject.trim() || !message.trim()}
                >
                  {sending ? (
                    <>
                      <span className="button-spinner"></span>
                      <span className="button-text">Sending...</span>
                    </>
                  ) : (
                    <>
                      <span className="button-icon">📤</span>
                      <span className="button-text">
                        Send to {targetGroup === 'all_users' ? 'All Users' : 
                                targetGroup === 'paid_users' ? 'Paid Users' :
                                targetGroup === 'questionnaire_completed' ? 'Questionnaire Users' :
                                'Users Without Questionnaire'}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="page-footer">
        <div className="footer-content">
          <p className="footer-text">
            <span className="footer-icon">⚠️</span>
            This will send emails to the selected user group. Please review your message carefully before sending.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BulkEmailPage;